import { useEffect, useCallback } from 'react';

export default function Lightbox({
  images = [],
  currentIndex = 0,
  isOpen = false,
  onClose,
  onNavigate
}) {
  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNavigate((currentIndex + 1) % images.length);
      if (e.key === 'ArrowLeft')
        onNavigate((currentIndex - 1 + images.length) % images.length);
    },
    [isOpen, currentIndex, images.length, onClose, onNavigate]
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen || images.length === 0) return null;

  const currentImg = images[currentIndex];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning i fuld størrelse"
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 select-none animate-in fade-in duration-200"
      onClick={onClose}
    >
      {/* Top Bar with counter & close */}
      <div
        className="absolute top-4 inset-x-4 sm:inset-x-6 flex items-center justify-between text-white z-10 pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-sm font-medium bg-black/50 px-3 py-1.5 rounded-full border border-white/10">
          {currentIndex + 1} / {images.length}
        </span>

        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400"
          aria-label="Luk billedvisning"
        >
          <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
        </button>
      </div>

      {/* Main Image Container */}
      <div
        className="relative max-w-5xl max-h-[82vh] w-full h-full flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={currentImg}
          alt={`Bådtransport billede ${currentIndex + 1}`}
          className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl transition-all"
        />

        {/* Previous Button */}
        {images.length > 1 && (
          <button
            type="button"
            onClick={() =>
              onNavigate((currentIndex - 1 + images.length) % images.length)
            }
            className="absolute left-2 sm:-left-12 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center border border-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left" aria-hidden="true" />
          </button>
        )}

        {/* Next Button */}
        {images.length > 1 && (
          <button
            type="button"
            onClick={() => onNavigate((currentIndex + 1) % images.length)}
            className="absolute right-2 sm:-right-12 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center border border-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Bottom Hint */}
      <div className="absolute bottom-4 inset-x-0 text-center text-xs text-slate-400 pointer-events-none">
        Brug piletaster eller swipe for at navigere · Klik hvor som helst for at lukke
      </div>
    </div>
  );
}
